import { useEffect, useRef, useState } from "react";
import { readAuth, onAuthChanged, clearAuth } from "./auth";
import { fetchMe } from "./api";

/**
 * `/me` cache + hook — see 2026-02-02 production auth pass in memory
 * for the full RCA.  Key invariants:
 *
 *   * SUCCESS         → cache the user for 60 s.
 *   * DEFINITIVE      → server said 401/403 → cache null, hook clears
 *     LOGOUT             cc_auth, chip flips to signed-out.
 *   * TRANSIENT       → network error/timeout/abort, 429, 5xx, any
 *     RETRY              other non-401/403: DO NOT cache, DO NOT clear
 *                        the token, keep the chip populated using the
 *                        LAST KNOWN identity (either the live cache or
 *                        the identity persisted alongside the token in
 *                        `cc_auth` localStorage). Schedule an
 *                        auto-retry via `cc-auth-changed` so the chip
 *                        refreshes to fresh /me data once the backend
 *                        recovers — no navigation required.
 *
 * Tester follow-up (2026-02-03 header-UI pass):
 * The previous version preserved the token during a transient failure
 * but let `user` fall back to `null` in state, so the header UserChip
 * disappeared and the sign-in button appeared while the outage lasted
 * — the user *looked* signed out. Fix: prefer the persisted
 * `readAuth().user` (or the pre-transient `_cache.user`) so the chip
 * stays visible across the entire transient window.
 */

const TTL_MS = 60_000;
const RETRY_MS = 5_000;
const TRANSIENT = Symbol("me-transient-failure");

let _cache = null;           // { at: number, user: object|null }
let _inflight = null;        // Promise<user|null|TRANSIENT> | null
let _retryHandle = null;     // number | null

function invalidate() {
  _cache = null;
  _inflight = null;
  if (_retryHandle) {
    clearTimeout(_retryHandle);
    _retryHandle = null;
  }
}

function scheduleRetry() {
  if (_retryHandle) return;                    // already queued
  if (typeof window === "undefined") return;
  _retryHandle = setTimeout(() => {
    _retryHandle = null;
    _cache = null;                             // force a fresh /me next tick
    _inflight = null;
    // Fire the auth-changed event so every mounted useAuth() re-runs its
    // refresh() and picks up the new /me result — no navigation needed.
    window.dispatchEvent(new Event("cc-auth-changed"));
  }, RETRY_MS);
}

if (typeof window !== "undefined") {
  window.addEventListener("cc-auth-changed", invalidate);
  window.addEventListener("storage", invalidate);
}

function isDefinitiveLogout(err) {
  const resp = err && err.response;
  if (!resp) return false;                     // network error / abort / timeout
  const status = resp.status;
  if (status === 401 || status === 403) return true;
  return false;                                 // 429, 5xx, other 4xx = transient
}

async function getMe() {
  if (_cache && Date.now() - _cache.at < TTL_MS) return _cache.user;
  if (_inflight) return _inflight;

  const auth = readAuth();
  if (!auth?.token) {
    _cache = { at: Date.now(), user: null };
    return null;
  }

  _inflight = (async () => {
    try {
      const user = await fetchMe();
      _cache = { at: Date.now(), user };
      return user;
    } catch (err) {
      if (isDefinitiveLogout(err)) {
        _cache = { at: Date.now(), user: null };
        return null;
      }
      // Transient — schedule an auto-retry so the chip refreshes even
      // if the user doesn't navigate.
      scheduleRetry();
      return TRANSIENT;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * React hook — mirrors `/me` into React state.
 */
export function useAuth() {
  const [state, setState] = useState(() => {
    const stored = readAuth();
    // Prefer any live cached user; otherwise the persisted identity that
    // ships alongside the token in localStorage. This keeps the chip
    // visible across page reloads even before /me completes.
    const initialUser = _cache?.user ?? stored?.user ?? null;
    return {
      token: stored?.token || null,
      user: initialUser,
      loading: _cache === null,
    };
  });

  useEffect(() => {
    let alive = true;

    async function refresh() {
      const stored = readAuth();
      const result = await getMe();
      if (!alive) return;

      // Transient — keep whatever identity we already believe in.
      if (result === TRANSIENT) {
        setState((prev) => {
          // If we already have a live user object in state, keep it.
          // Otherwise fall back to the identity persisted alongside the
          // token in localStorage. Only truly-unknown identities (no
          // cached user AND no persisted user) fall through to `null`.
          const preservedUser =
            prev.user ?? _cache?.user ?? stored?.user ?? null;
          return {
            token: stored?.token || null,
            user: preservedUser,
            loading: false,
          };
        });
        return;
      }

      // Definitive logout — server 401/403 with a valid token stored.
      if (result === null && stored?.token) {
        clearAuth();
        setState({ token: null, user: null, loading: false });
        return;
      }

      setState({
        token: stored?.token || null,
        user: result,
        loading: false,
      });
    }

    refresh();
    const unsub = onAuthChanged(() => refresh());
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  return state;
}

/** Force a re-fetch (called after uploads / edits that change /me state). */
export function refreshMe() {
  invalidate();
}
